// ============================================================================
// An item's decision write-up, split into the numbered blocks the approved
// design shows (item 701; tcs-command-center-interactions-2026-10-05.html and
// the 6 Oct correction): where it came from, what was checked, options,
// recommendation, if approved, demo.
//
// The bridge hands over the Assessor's comment in full. This splits it on its
// own "## " headings and pulls the "**Demo**" line out into its own block.
// Pure, no React: view.tsx draws what this returns, and never as raw HTML.
// ============================================================================

export interface WriteUpBlock {
  heading: string;
  text: string;
}

/** The Assessor's headings, worded for Ben where the heading speaks as the bot. */
const RENAME: Record<string, string> = {
  'What I checked': 'What was checked',
};

export function splitWriteUp(body: string): { blocks: WriteUpBlock[]; demo: string | null } {
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const blocks: WriteUpBlock[] = [];
  let cur: WriteUpBlock | null = null;
  let demo: string | null = null;
  for (const line of lines) {
    const h = /^##\s+(.+?)\s*$/.exec(line);
    if (h) {
      cur = { heading: RENAME[h[1]] ?? h[1], text: '' };
      blocks.push(cur);
      continue;
    }
    if (/^\s*\*\*Demo\*\*/.test(line)) {
      demo = demo ? `${demo}\n${line.trim()}` : line.trim();
      continue;
    }
    if (cur) cur.text += `${line}\n`;
  }
  const kept = blocks
    .map((b) => ({ heading: b.heading, text: b.text.trim() }))
    .filter((b) => b.text !== '' && b.heading.toLowerCase() !== 'demo');
  const demoBlock = blocks.find((b) => b.heading.toLowerCase() === 'demo');
  if (!demo && demoBlock && demoBlock.text.trim()) demo = demoBlock.text.trim();
  return { blocks: kept, demo };
}

// ----- a small, safe reading of the markdown the bots write ------------------------

export type Inline = { kind: 'text' | 'bold' | 'italic' | 'code'; text: string };
export type Para = { kind: 'p'; parts: Inline[] } | { kind: 'list'; items: Inline[][] };

export function inline(src: string): Inline[] {
  const out: Inline[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|(?<![\w*])\*[^*\s][^*]*\*(?![\w*]))/g;
  let last = 0;
  for (const m of src.matchAll(re)) {
    if (m.index! > last) out.push({ kind: 'text', text: src.slice(last, m.index) });
    const t = m[0];
    if (t.startsWith('**')) out.push({ kind: 'bold', text: t.slice(2, -2) });
    else if (t.startsWith('`')) out.push({ kind: 'code', text: t.slice(1, -1) });
    else out.push({ kind: 'italic', text: t.slice(1, -1) });
    last = m.index! + t.length;
  }
  if (last < src.length) out.push({ kind: 'text', text: src.slice(last) });
  return out;
}

export function paragraphs(text: string): Para[] {
  const out: Para[] = [];
  let para: string[] = [];
  let list: Inline[][] | null = null;
  const flush = () => {
    if (para.length) out.push({ kind: 'p', parts: inline(para.join(' ')) });
    para = [];
  };
  const endList = () => {
    if (list) out.push({ kind: 'list', items: list });
    list = null;
  };
  for (const raw of text.split('\n')) {
    const l = raw.trimEnd();
    const item = /^\s*(?:[-*]|\d+[.)])\s+(.*)$/.exec(l);
    if (item) {
      flush();
      if (!list) list = [];
      list.push(inline(item[1]));
      continue;
    }
    if (l.trim() === '') {
      flush();
      endList();
      continue;
    }
    if (list && /^\s{2,}\S/.test(raw)) {
      // a wrapped line of the list item above
      const lastItem = list[list.length - 1];
      lastItem.push({ kind: 'text', text: ' ' }, ...inline(l.trim()));
      continue;
    }
    endList();
    para.push(l.replace(/^#+\s*/, '').trim());
  }
  flush();
  endList();
  return out;
}

// ----- The build route (item 702, decision 5) ---------------------------------------

export interface BuildRoute {
  /** What the write-up recommends; null when the write-up predates the heading. */
  route: 'Builder' | 'Cowork' | null;
  /** The reason, in Ben's words, from the same line. */
  reason: string;
  /** The "Parts of the app:" line, as the catalog names them. */
  parts: string[];
}

/**
 * The write-up's last heading, "## Build route": one line, "Builder" or "Cowork"
 * and why, then "Parts of the app: …". Write-ups older than the heading have
 * none, and Approve then starts on Cowork.
 */
export function buildRoute(body: string | null | undefined): BuildRoute {
  const none: BuildRoute = { route: null, reason: '', parts: [] };
  if (!body) return none;
  const block = splitWriteUp(body).blocks.find((b) => /^build route$/i.test(b.heading));
  if (!block) return none;
  const lines = block.text.split('\n').map((l) => l.replace(/^\s*[-*]\s+/, '').replace(/\*\*/g, '').trim()).filter(Boolean);
  const partsLine = lines.find((l) => /^parts of the app\s*:/i.test(l));
  const first = lines.find((l) => l !== partsLine) ?? '';
  const m = /^(Builder|Cowork)\b[\s:.—–-]*(.*)$/i.exec(first);
  return {
    route: m ? ((m[1][0].toUpperCase() + m[1].slice(1).toLowerCase()) as 'Builder' | 'Cowork') : null,
    reason: m ? m[2].trim() : first,
    parts: partsLine
      ? partsLine
          .replace(/^parts of the app\s*:/i, '')
          .split(/[,;·]/)
          .map((x) => x.trim().replace(/\.$/, ''))
          .filter(Boolean)
      : [],
  };
}
